import { Spacing } from '@persona-id/design-tokens';
import { get } from 'lodash/fp';
import styled from 'styled-components';

import { CONFIG_STYLE_COMPONENTS } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-constants';
import useSubmitStyleVariant from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/hooks/useSubmitStyleVariants';
import BorderStyles from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/BorderStyles';
import ColorStyles from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/ColorStyles';
import ConfigSizingStyles from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/ConfigSizingStyles';
import FooterLayoutStyles from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/FooterLayoutStyles';
import LayoutStyles from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/LayoutStyles';
import NestedChildLayoutStyle from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/NestedChildLayoutStyle';
import ParentLayoutStyles from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/ParentLayoutStyles';
import SizingStyles, {
  STATIC_FULL_WIDTH_PX,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/SizingStyles';
import SpacingStyles from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/SpacingStyles';
import TextStyles from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/TextStyles';
import {
  COMPONENT_SCHEMA,
  isSupportedComponentConfigLibraryType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/constants';
import {
  ClientOnlyComponentSchemaRecord,
  isClientDefinedComponentSchema,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import CodeEditor from 'dashboard/components-lib/CodeEditor';
import { Paragraph } from 'design-system/components/Text';
import {
  GOV_ID_OPTION_IMAGE_ASSETS,
  IMAGE_LOCAL_DIMENSION_DEFAULTS,
} from 'inquiry/components/ImageLocal/ImageLocal';
import { IMAGE_REMOTE_DIMENSION_DEFAULTS } from 'inquiry/components/ImageRemote/ImageRemote';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import { StyleValueFramePositioning, StyleValueSizeConfig } from 'lib/inquiryStyles';
import { StepUi } from 'lib/interfaces/StepsConfig';
import {
  BorderStylesComponent,
  BorderStylesComponentConfig,
  canStyleBorders,
  canStyleColors,
  canStyleHeight,
  canStyleLayout,
  canStyleParentLayout,
  canStyleSpacing,
  canStyleText,
  canStyleWidth,
  FooterComponent,
  ImageKey,
  ImageLocalComponentConfig,
  isStepperComponentConfig,
  isStylableUiParentComponent,
  isStylableUiParentComponentConfig,
  isTextComponentConfig,
  isUiButtonComponentConfig,
  StackComponentStyles,
  StepperComponentConfig,
  TextStylingComponent,
  TextStylingComponentConfig,
  UiComponent,
  UiComponentConfig,
  UiComponentConfigType,
  UiComponentType,
} from 'lib/interfaces/ui-components';
import { captureMessage } from 'lib/reporting';

const SIZE_COMPONENTS_WITH_FALLBACK = [
  UiComponentConfigType.ImageHosted,
  UiComponentConfigType.ImageLocal,
  UiComponentConfigType.HorizontalStack,
  UiComponentConfigType.ClickableStack,
];

const getSizingFallbackDimensions = (
  componentType: UiComponentConfigType,
  { key }: { key?: ImageKey },
) => {
  if ([UiComponentConfigType.ImageHosted].includes(componentType)) {
    return IMAGE_REMOTE_DIMENSION_DEFAULTS;
  } else if ([UiComponentConfigType.ImageLocal].includes(componentType)) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return IMAGE_LOCAL_DIMENSION_DEFAULTS[key];
  } else if (
    [UiComponentConfigType.HorizontalStack, UiComponentConfigType.ClickableStack].includes(
      componentType,
    )
  ) {
    return {
      width: {
        value: STATIC_FULL_WIDTH_PX,
        unit: 'px',
      } as StyleValueSizeConfig,
      height: {
        value: 0,
        unit: 'px',
      } as StyleValueSizeConfig,
    };
  } else {
    return null;
  }
};

const getHiddenColorFieldsForLocalImage = (component: ImageLocalComponentConfig) => {
  const imageKey = component.config['image-key'];
  if ([ImageKey.AnimatedCheck].includes(imageKey)) {
    return ['fill-color--highlight', 'stroke-color'];
  } else if (GOV_ID_OPTION_IMAGE_ASSETS.includes(imageKey)) {
    return ['fill-color--highlight', 'fill-color--background'];
  } else if ([ImageKey.OptionChevronIcon].includes(imageKey)) {
    return ['fill-color', 'fill-color--highlight', 'fill-color--background'];
  } else if ([ImageKey.OptionCheckIcon].includes(imageKey)) {
    return ['fill-color--highlight', 'fill-color--background'];
  } else {
    return [];
  }
};

// Note (Evan): need to figure out how to accurately get image_remote
// component config from the server. This models what the component data
// would look like if it were fetched from /component-schema
const SOURCE_TRUTH_COMPONENT_BASE: ClientOnlyComponentSchemaRecord = {
  [UiComponentConfigType.ImageHosted]: {
    config: {
      config: {
        hidden: null,
        'hidden-server-evaluated': null,
      },
      type: UiComponentConfigType.ImageHosted,
      'style-variants': [
        {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          height: null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          width: null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          justify: null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          margin: null,
          namespace: 'base',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'fill-color--fill': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'fill-color--background': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'fill-color--highlight': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'fill-color--original-fill': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'fill-color--original-background': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'fill-color--original-highlight': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'stroke-color--stroke': null,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'stroke-color--original-stroke': null,
        },
      ],
    },
    preview: {},
  },
  [UiComponentConfigType.Stepper]: {
    config: {
      type: UiComponentConfigType.Stepper,
      config: {},
      'style-variants': [
        {
          namespace: 'base',
        },
      ],
    } as StepperComponentConfig,
    preview: {},
  },
};

const UneditableStylingContainer = styled.div`
  padding: ${Spacing[4]} ${Spacing[5]};
`;

const StylingErrorFallbackState = () => (
  <UneditableStylingContainer>
    <Paragraph color='secondary' size='sm'>
      Styles for this component cannot be edited at this time.
    </Paragraph>
  </UneditableStylingContainer>
);

type ComponentEditorStyles = {
  component: UiComponentConfig;
  componentPreview: UiComponent;
  componentBasePath: string;
  stepPreview: StepUi;
  namespace?: string;
  editable: boolean;
};

const ComponentEditorStyles = ({
  component,
  componentPreview,
  componentBasePath,
  stepPreview,
  namespace = 'base',
  editable,
}: ComponentEditorStyles) => {
  const useComponentStyles = useFeatureFlag('inquiry:use-component-styles');
  const enableStepperConfigEditor = useFeatureFlag('dashboard:ite-enable-stepper-config-editor');

  let sizeSection;
  let spaceSection;
  let layoutSection;
  let colorSection;
  let textSection;
  let borderSection;

  const componentSchemaDefinedComponent = isClientDefinedComponentSchema(component.type)
    ? SOURCE_TRUTH_COMPONENT_BASE[component.type]
    : isSupportedComponentConfigLibraryType(component.type)
      ? COMPONENT_SCHEMA[component.type]
      : null;

  let styleVariantIndex = component['style-variants']?.findIndex(
    ({ namespace: variantNamespace }) => variantNamespace === namespace,
  );

  if (styleVariantIndex == null || styleVariantIndex === -1) {
    styleVariantIndex = 0;
  }

  const { submitStyle, path } = useSubmitStyleVariant(
    component,
    componentBasePath,
    styleVariantIndex,
    namespace,
  );

  if (componentSchemaDefinedComponent == null) {
    captureMessage('Component does not have styles at namespace', 'warning', {
      component: component.type,
      namespace: namespace ?? 'base',
    });
    return <StylingErrorFallbackState />;
  }

  const baseComponentStyleConfig = {
    ...componentSchemaDefinedComponent.config,
    name: 'component_styling',
  };

  const canStyleComponentWidth = canStyleWidth(baseComponentStyleConfig);
  const canStyleComponentHeight = canStyleHeight(baseComponentStyleConfig);

  if (canStyleComponentWidth || canStyleComponentHeight) {
    const isSpacerComponent = component.type === UiComponentConfigType.Spacer;
    const isContainerComponent = isStylableUiParentComponentConfig(component);

    // for parent components, we can only style width
    // when the frame positioning is not set to `edge-to-edge`
    const isEdgeFramePositioning =
      (componentPreview.styles as StackComponentStyles)?.['frame-positioning']?.base?.base ===
      StyleValueFramePositioning.EdgeToEdge;

    const fallbackDimensions = SIZE_COMPONENTS_WITH_FALLBACK.includes(component.type)
      ? getSizingFallbackDimensions(component.type, {
          key: (component as ImageLocalComponentConfig).config?.['image-key'] ?? null,
        })
      : null;

    const sharedSizingComponentProps = {
      component,
      componentBasePath,
      editable,
      styleVariantIndex,
      submitStyle,
      fallbackDimensions,
    };

    if (CONFIG_STYLE_COMPONENTS.includes(component.type)) {
      const SizingSection = useComponentStyles ? SizingStyles : ConfigSizingStyles;
      sizeSection = (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        <SizingSection
          {...sharedSizingComponentProps}
          {...(useComponentStyles && {
            componentPreview,
          })}
          widthOptions={{
            hidden: isSpacerComponent,
            disabled: isContainerComponent && isEdgeFramePositioning,
          }}
          heightOptions={{
            hidden: isContainerComponent,
            hideInstrinsicOption: isSpacerComponent,
          }}
        />
      );
    } else {
      sizeSection = (
        <SizingStyles
          {...sharedSizingComponentProps}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          componentPreview={componentPreview}
          widthOptions={{
            hidden: isSpacerComponent,
            disabled: isContainerComponent && isEdgeFramePositioning,
          }}
          heightOptions={{
            hidden: isContainerComponent,
            hideInstrinsicOption: isUiButtonComponentConfig(component) || isSpacerComponent,
          }}
        />
      );
    }
  }

  if (isStepperComponentConfig(component)) {
    if (enableStepperConfigEditor) {
      return (
        <CodeEditor
          code={JSON.stringify(component?.['style-variants']?.[0], null, 2)}
          language='json'
          readOnly={!editable}
          onChange={(value) => {
            submitStyle(null, JSON.parse(value));
          }}
        />
      );
    }
    return <StylingErrorFallbackState />;
  }

  if (canStyleSpacing(baseComponentStyleConfig)) {
    spaceSection = (
      <SpacingStyles
        componentPreview={componentPreview}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        component={component}
        editable={editable}
        styleVariantIndex={styleVariantIndex}
        baseComponentConfig={baseComponentStyleConfig}
        submitStyle={submitStyle}
      />
    );
  }

  if (canStyleLayout(baseComponentStyleConfig) && !isTextComponentConfig(component)) {
    if (
      isStylableUiParentComponentConfig(component) &&
      canStyleParentLayout(baseComponentStyleConfig) &&
      isStylableUiParentComponent(componentPreview)
    ) {
      layoutSection = (
        <ParentLayoutStyles
          componentPreview={componentPreview}
          component={component}
          editable={editable}
          styleVariantIndex={styleVariantIndex}
          submitStyle={submitStyle}
        />
      );
    } else {
      layoutSection = (
        <LayoutStyles
          componentPreview={componentPreview}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          component={component}
          editable={editable}
          styleVariantIndex={styleVariantIndex}
          baseComponentConfig={baseComponentStyleConfig}
          submitStyle={submitStyle}
        />
      );
    }
  }

  if (component.type === UiComponentConfigType.Footer) {
    layoutSection = (
      <FooterLayoutStyles
        component={component}
        editable={editable}
        componentPath={componentBasePath}
        componentPreview={componentPreview as FooterComponent}
        submitStyle={submitStyle}
      />
    );
  }

  // Check to see if we have a nested component
  const parentComponentPath = componentBasePath.split('.').slice(0, -3);

  if (parentComponentPath.length > 1) {
    const parentPreviewPath = [...parentComponentPath]
      .join('.')
      .replace(/config/g, 'attributes')
      .replace(/components/g, 'children')
      .split('.')
      .slice(3);

    const parentComponentPreview = get(parentPreviewPath, stepPreview.config.components);

    /**
     * ClickableStack and HorizontalStack parent components have child-sizes
     *  which determines how a component will flex within the container.
     *  We set a default for new components, but allow for controlling how these
     *  components will flex within the container, rather than leaving it up to the container
     *  to decide how all of its children will flex.
     * */
    if (parentComponentPreview != null && parentComponentPreview.type !== UiComponentType.Footer) {
      const nestedIndex = parentComponentPreview.attributes.children.findIndex(
        (childComponent: UiComponent) => childComponent.name === component.name,
      );

      layoutSection = (
        <>
          {/** Include any previous layout section */}
          {layoutSection}
          <NestedChildLayoutStyle
            parentComponentPreview={parentComponentPreview}
            nestedIndex={nestedIndex}
            editable={editable}
            styleVariantIndex={styleVariantIndex}
            parentComponentPath={parentComponentPath.join('.')}
          />
        </>
      );
    }
  }

  if (canStyleColors(baseComponentStyleConfig)) {
    colorSection = (
      <ColorStyles
        componentPreview={componentPreview}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        component={component}
        editable={editable}
        styleVariantIndex={styleVariantIndex}
        baseComponentConfig={baseComponentStyleConfig}
        submitStyle={submitStyle}
        /**
         * Note (Evan): We hide the following attributes for IMAGE_LOCAL
         * because for the certain images, value updates do not update the
         * preview when they are changed
         *
         * @see front-end/inquiry/components/ImageLocal/ImageLocal.tsx:111
         */
        {...(component.type === UiComponentConfigType.ImageLocal && {
          hiddenFields: getHiddenColorFieldsForLocalImage(component),
        })}
      />
    );
  }

  if (canStyleText(baseComponentStyleConfig)) {
    textSection = (
      <TextStyles
        component={component as TextStylingComponentConfig}
        baseComponentConfig={baseComponentStyleConfig}
        componentBasePath={componentBasePath}
        editable={editable}
        styleVariantIndex={styleVariantIndex}
        componentPreview={componentPreview as TextStylingComponent}
        submitStyle={submitStyle}
        disabledFields={isUiButtonComponentConfig(component) ? ['justify'] : []}
        path={path}
      />
    );
  }

  if (canStyleBorders(baseComponentStyleConfig)) {
    borderSection = (
      <BorderStyles
        component={component as BorderStylesComponentConfig}
        editable={editable}
        styleVariantIndex={styleVariantIndex}
        componentPreview={componentPreview as BorderStylesComponent}
        submitStyle={submitStyle}
      />
    );
  }

  return (
    <>
      {sizeSection}
      {spaceSection}
      {layoutSection}
      {textSection}
      {colorSection}
      {borderSection}
    </>
  );
};

export default ComponentEditorStyles;
